import client from './client'
import type {
  FinanceFilters,
  FinanceOverview,
  FinanceTransaction,
  FinanceTransactionParams,
  PaginatedResponse,
  PlatformFeePreview,
} from '@/types/finance'

function base(organisationId: number): string {
  return `/organizer/organisations/${organisationId}/finance`
}

export async function fetchFinanceOverview(
  organisationId: number,
  params: FinanceFilters,
): Promise<FinanceOverview> {
  const queryParams = buildQueryParams(params)
  const { data } = await client.get<{ data: FinanceOverview }>(`${base(organisationId)}/overview`, {
    params: queryParams,
  })
  return data.data
}

export async function fetchFinanceTransactions(
  organisationId: number,
  params: FinanceTransactionParams,
): Promise<PaginatedResponse<FinanceTransaction>> {
  const queryParams = buildTransactionQueryParams(params)
  const { data } = await client.get<{
    data: FinanceTransaction[]
    meta: PaginatedResponse<FinanceTransaction>['meta']
  }>(`${base(organisationId)}/transactions`, { params: queryParams })
  return { data: data.data, meta: data.meta }
}

export async function startFinanceExport(
  organisationId: number,
  params: FinanceFilters & { format: 'csv' | 'xlsx' },
): Promise<{ message: string }> {
  const payload = buildQueryParams(params)
  const { data } = await client.post<{ message: string }>(`${base(organisationId)}/exports`, payload)
  return data
}

export async function fetchPlatformFeePreview(
  organisationId: number,
  amount: string,
): Promise<PlatformFeePreview> {
  const { data } = await client.get<{ data: PlatformFeePreview }>(
    `${base(organisationId)}/platform-fee-preview`,
    { params: { amount } },
  )
  return data.data
}

function buildQueryParams(filters: FinanceFilters): Record<string, string | number> {
  const params: Record<string, string | number> = {}
  if (filters.date_from !== null) params['date_from'] = filters.date_from
  if (filters.date_to !== null) params['date_to'] = filters.date_to
  if (filters.event_id !== null) params['event_id'] = filters.event_id
  if (filters.edition_id !== null) params['edition_id'] = filters.edition_id
  if (filters.race_id !== null) params['race_id'] = filters.race_id
  if (filters.payment_method !== null) params['payment_method'] = filters.payment_method
  return params
}

function buildTransactionQueryParams(
  params: FinanceTransactionParams,
): Record<string, string | number> {
  const base = buildQueryParams(params)
  if (params.type != null) base['type'] = params.type
  if (params.sort != null) base['sort'] = params.sort
  if (params.per_page != null) base['per_page'] = params.per_page
  if (params.page != null) base['page'] = params.page
  return base
}
